import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    effect,
    EventEmitter,
    HostBinding,
    inject,
    input,
    Input,
    Output,
    signal,
    ViewChild,
} from '@angular/core';
import { IBarcodeSearchFacade, IEntity } from '@pf/shared-common';
import { InputComponent, PfStyle, SharedUiModule } from '@pf/shared-ui';
import { FormControl } from '@angular/forms';
import { Subject, take, tap } from 'rxjs';
import { CpwInputErrorService } from '../services/cpw-input-error.service';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

export type BarcodeScanEvent = {
    scanInput: string;
    entity?: IEntity;
    processed: Subject<boolean>;
};

@Component({
    selector: 'cpw-scan-input',
    imports: [SharedUiModule, NzSpinComponent],
    templateUrl: './cpw-scan-input.component.html',
    styleUrl: './cpw-scan-input.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwScanInputComponent implements AfterViewInit {
    private readonly inputErrorService = inject(CpwInputErrorService);

    @ViewChild(InputComponent) scanInput!: InputComponent;

    readonly inputStyle = input<PfStyle>('compact');
    readonly label = input<string>('Scan or enter barcode');
    readonly autoFocus = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    readonly expectedBarcode = input<string | string[] | undefined>();
    readonly notFoundMessage = input<string>('Invalid scan!');
    readonly defaultScanError = input<string>('');
    readonly value = input<string | undefined>();
    @Input()
    barcodeSearchFacade!: IBarcodeSearchFacade<IEntity>;
    @Output() barcodeScanned = new EventEmitter<BarcodeScanEvent>();

    readonly scanError = signal('');
    readonly processingScan = signal(false);

    /**
     * Set while the component itself moves focus or clears the field (opening
     * the error drawer, refocusing, clearing). Used to ignore the resulting
     * `focusout` so a programmatic focus change is not treated as a scan.
     */
    private suppressBlurScan = false;
    /** Last value processed via a scan — used to dedupe blur re-submissions. */
    private lastScannedValue: string | null = null;

    @HostBinding('class.scan-shake')
    get hasScanError(): boolean {
        return !!this.scanError();
    }
    readonly barcodeControl = new FormControl<string>('');

    constructor() {
        this.registerScanErrorDrawer();
    }

    ngAfterViewInit(): void {
        if (this.value()) {
            this.barcodeControl.setValue(this.value()!);
        }
        if (this.autoFocus()) {
            this.focus(true);
        }
    }

    focus(selectText: boolean, clearInput?: boolean) {
        this.armBlurSuppression();
        this.scanInput.focusWithoutKeyboard(selectText);
        this.scanInput.scrollIntoView();

        if (clearInput) {
            this.barcodeControl.setValue('');
            this.lastScannedValue = null;
        }
    }

    /**
     * Handles `focusout`. Ignores focus changes the component triggered itself
     * and de-dupes a blur that carries the same value already scanned, then
     * defers to the normal scan handler for genuine user blurs.
     */
    onBlurScan($event: Event) {
        if (this.suppressBlurScan) {
            this.suppressBlurScan = false; // consume once
            return;
        }

        const value = (($event.target as HTMLInputElement)?.value ?? '').trim();
        if (value && value === this.lastScannedValue) {
            return;
        }

        this.onBarcodeScanned($event);
    }

    onBarcodeScanned($event: Event) {
        const input = ($event as KeyboardEvent).target as HTMLInputElement;
        const barcode = input.value.trim();

        if (!barcode) {
            return;
        }

        if (this.processingScan()) {
            return;
        }

        this.lastScannedValue = barcode;

        if (!this.validateExpectedBarcode(barcode)) {
            this.scanError.set('Invalid scan!');
            this.scanInput.focusWithoutKeyboard();
            return;
        }

        if (!this.barcodeSearchFacade) {
            this.emitScanEvent(barcode);
            return;
        }

        this.processingScan.set(true);
        this.barcodeSearchFacade
            .searchByBarcode$(barcode)
            .pipe(
                tap({
                    next: entity => {
                        this.validateAndEmitScan(barcode, entity);
                    },
                    error: () => {
                        this.processingScan.set(false);
                        this.scanError.set(this.notFoundMessage());
                    },
                })
            )
            .subscribe();
    }

    private validateExpectedBarcode(barcode: string) {
        if (!this.expectedBarcode()) {
            return true;
        }

        const expectedBarcode = this.expectedBarcode() as string | string[];
        if (typeof expectedBarcode === 'string') {
            return expectedBarcode === barcode;
        }

        if (expectedBarcode.length === 0) {
            return true;
        }

        return expectedBarcode.includes(barcode);
    }

    private getExpectedBarcodeString() {
        if (!this.expectedBarcode()) {
            return '';
        }

        const expectedBarcode = this.expectedBarcode() as string | string[];
        if (typeof expectedBarcode === 'string') {
            return expectedBarcode;
        }
        return expectedBarcode.join(' OR ');
    }

    private validateAndEmitScan(barcode: string, entity: IEntity | null) {
        if (!entity?.id) {
            this.processingScan.set(false);
            this.scanError.set(this.notFoundMessage());
            return;
        }

        this.emitScanEvent(barcode, entity);
    }

    private emitScanEvent(barcode: string, entity?: IEntity) {
        this.scanError.set('');
        this.processingScan.set(true);
        const processScanSubject = new Subject<boolean>();
        let resolved = false;
        processScanSubject
            .pipe(
                tap(() => {
                    resolved = true;
                    this.processingScan.set(false);
                }),
                tap(success => {
                    if (!success) {
                        this.scanErrorClosed();
                    }
                }),
                take(1)
            )
            .subscribe();
        this.barcodeScanned.emit({
            entity,
            scanInput: barcode,
            processed: processScanSubject,
        });
        // Auto-reset if consumer doesn't call processed.next()
        setTimeout(() => {
            if (!resolved) {
                this.processingScan.set(false);
            }
        }, 5000);
    }

    onPaste(event: ClipboardEvent) {
        const pasted = event.clipboardData?.getData('text')?.trim();
        if (!pasted) return;
        event.preventDefault();
        this.barcodeControl.setValue(pasted);
    }

    private armBlurSuppression() {
        this.suppressBlurScan = true;
        // Safety net in case the expected programmatic focusout never fires.
        setTimeout(() => (this.suppressBlurScan = false), 200);
    }

    private scanErrorClosed() {
        this.armBlurSuppression();
        this.scanError.set(this.defaultScanError());
        this.barcodeControl.reset();
        this.lastScannedValue = null;
        setTimeout(() => {
            this.scanInput.focusWithoutKeyboard();
        });
    }

    private registerScanErrorDrawer() {
        effect(() => {
            if (this.scanError().length > 0) {
                // Opening the drawer steals focus from the input; ignore the
                // resulting focusout so it is not processed as a scan.
                this.armBlurSuppression();
                this.inputErrorService.showError(
                    this.scanError(),
                    this.barcodeControl.value || '',
                    this.getExpectedBarcodeString(),
                    () => {
                        this.scanErrorClosed();
                    }
                );
            }
        });
    }
}
